import axiosInstance, { axiosInstanceCached } from '@/utils/axios';
import Configuration from '@/utils/configuration';
import { handleApiError, deleteDataCache } from '@/utils/helpers';

const GOOGLE_MAPS_KEY = Configuration.value('VUE_APP_GOOGLE_MAPS_KEY');
const SITE_ENV = Configuration.value('VUE_APP_SITE_ENV');
const qaSite = 'https://qa.e6golf.com';
const prodSite = 'https://portal.e6golf.com';
const APP_HOST = SITE_ENV === 'staging' ? qaSite : SITE_ENV === 'production' ? prodSite : qaSite;

export const list = async (payload) => {
  try {
    let response = {};
    await deleteDataCache(axiosInstanceCached, payload?.deleteCache, 'clubhouse_list');

    try {
      response = await axiosInstanceCached.get(`/clubhouses`, {
        id: 'clubhouse_list',
        cache: {
          ttl: 1000 * 60 * 15,
        },
      });
      return response;
    } catch (error) {
      return handleApiError(error);
    }
  } catch (err) {
    return err;
  }
};

export const show = async (id, overrideCache = false) => {
  let response = {};

  try {
    response = await axiosInstanceCached.get(`/clubhouse/${id}`, {
      id: `clubhouse_${id}`,
      cache: {
        ttl: 1000 * 60 * 15,
        override: overrideCache,
      },
    });
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Create a new clubhouse
// @ not used - switched to admin endpoint
export const create = async (payload) => {
  let response = {};

  try {
    response = await axiosInstance.post(`/clubhouse/create`, payload);
    await deleteDataCache(axiosInstanceCached, true, 'clubhouse_list');
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* update clubhouse information
export const update = async (payload) => {
  let response = {};

  try {
    response = await axiosInstance.patch(`/clubhouse/${payload.uuid}`, payload);
    await deleteDataCache(axiosInstanceCached, true, 'clubhouse_list');
    await deleteDataCache(axiosInstanceCached, true, `clubhouse_${payload.id}`);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* delete a clubhouse
export const deleteClubhouse = async (uuid) => {
  let response = {};

  try {
    response = await axiosInstance.delete(`/clubhouse/${uuid}`);
    await deleteDataCache(axiosInstanceCached, true, 'clubhouse_list');
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

export const listMembers = async (id) => {
  let response = {};

  try {
    response = await axiosInstance.get(`/clubmember/club/${id}`);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* get clubhouses for member uuid
export const listMemberClubs = async (id) => {
  let response = {};

  try {
    response = await axiosInstanceCached.get(`/clubmember/member/${id}`, {
      id: `member_club_${id}`,
      cache: {
        ttl: 1000 * 60 * 15,
      },
    });
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* delete member in a clubhouse
export const deleteMember = async (payload) => {
  let response = {};

  try {
    response = await axiosInstance.delete(`/clubmember`, { data: payload });
    await deleteDataCache(axiosInstanceCached, true, 'club_perms');
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* get clubmember details
export const getClubmember = async (userID) => {
  let response = {};

  try {
    response = await axiosInstance.get(`/clubmember/member/${userID}`);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* update clubmember
export const updateClubmember = async (payload) => {
  let response = {};

  try {
    response = await axiosInstance.patch(`/clubmember`, payload);
    await deleteDataCache(axiosInstanceCached, true, 'club_perms');
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* add clubmember
export const addClubmember = async (payload) => {
  let response = {};

  try {
    response = await axiosInstance.post(`/clubmember`, payload);
    await deleteDataCache(axiosInstanceCached, true, 'club_perms');
    await deleteDataCache(axiosInstanceCached, true, 'clubhouse_list');
    await deleteDataCache(axiosInstanceCached, true, `clubhouse_${payload.clubID}`);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* list all clubhouse licenses - Admin used
export const clublicenses = async ({ limit = '', offset = '' }) => {
  let response = {};

  try {
    response = await axiosInstanceCached.get(`/clublicenses?limit=${limit}&offset=${offset}`, {
      id: `adminClublicenses`,
      cache: {
        ttl: 1000 * 60 * 15, // 15 min
      },
    });
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* list licenses associated with a clubhouse - public clubhouses
export const listLicenses = async (id) => {
  let response = {};

  try {
    response = await axiosInstanceCached.get(`/clublicense/club/${id}`, {
      id: `listLicenses_${id}`,
      cache: {
        ttl: 1000 * 60 * 15, // 15 min
      },
    });
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* delete license associated with a clubhouse
export const deleteLicense = async (payload) => {
  let response = {};

  try {
    response = await axiosInstance.delete(`/clublicense`, { data: payload });
    await deleteDataCache(axiosInstanceCached, true, `listLicenses_${payload.club_uuid}`);
    await deleteDataCache(axiosInstanceCached, true, `adminClublicenses`);
    await deleteDataCache(axiosInstanceCached, true, `clubhouse_${payload.club_uuid}`);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* add new license associated with a clubhouse
export const addLicense = async (payload) => {
  let response = {};

  try {
    response = await axiosInstance.post(`/clublicense`, payload);
    await deleteDataCache(axiosInstanceCached, true, `listLicenses_${payload.club_uuid}`);
    await deleteDataCache(axiosInstanceCached, true, `adminClublicenses`);
    await deleteDataCache(axiosInstanceCached, true, `clubhouse_${payload.club_uuid}`);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* set clubmember handicap
export const setHandicap = async (payload) => {
  let response = {};

  try {
    response = await axiosInstance.patch(`/clubmember`, payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* create card for clubhouse
export const createCard = async (payload) => {
  let response = {};

  try {
    response = await axiosInstance.post(`/clubhouse/${payload.id}/card`, payload);
    await deleteDataCache(axiosInstanceCached, true, `club_cards_${payload.id}`);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* update card for clubhouse
export const updateCard = async (payload) => {
  let response = {};

  try {
    response = await axiosInstance.patch(
      `/clubhouse/${payload.id}/card/${payload.cardID}`,
      payload
    );
    await deleteDataCache(axiosInstanceCached, true, `club_cards_${payload.id}`);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* show individual card for clubhouse
export const showCard = async (payload) => {
  let response = {};

  try {
    response = await axiosInstance.get(`/clubhouse/${payload.id}/card/${payload.cardID}`);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* list all cards for clubhouse
export const listCards = async (payload) => {
  let response = {};

  try {
    response = await axiosInstanceCached.get(`/clubhouse/${payload}/card`, {
      id: `club_cards_${payload}`,
      cache: {
        ttl: 1000 * 60 * 10,
      },
    });
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* delete individual card for clubhouse
export const deleteCard = async (payload) => {
  let response = {};

  try {
    response = await axiosInstance.delete(`/clubhouse/${payload.id}/card/${payload.cardID}`);
    await deleteDataCache(axiosInstanceCached, true, `club_cards_${payload.id}`);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* get clubhouse coordinates (lat,lng)
export const getCoordinates = async (payload) => {
  let response = {};

  try {
    const mapUrl = 'https://maps.googleapis.com/maps/api/geocode/json?address=';
    response = await axiosInstance.get(
      `${mapUrl}${encodeURIComponent(payload)}&key=${GOOGLE_MAPS_KEY}`
    );
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

// @ Clubhouse emailing ==============================================

//* Retrieve email preview
export const emailPreview = async (payload) => {
  payload.AppHost = APP_HOST;
  let response = {};

  try {
    response = await axiosInstance.post(`/clubhouse/preview_email`, payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Send email
export const sendEmail = async (payload) => {
  payload.AppHost = APP_HOST;
  let response = {};

  try {
    response = await axiosInstance.post(`/clubhouse/send_email`, payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* show license/simulator status associated with a clubhouse
//* not used
export const simulatorStatus = async (payload) => {
  let response = {};

  try {
    response = await axiosInstance.get(`/simulators/${payload}/status`);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

// @ Clubhouse Global stats ==============================================

//* clubhouseUserStats return clubmember stats
export const clubhouseUserStats = async ({ clubID, fromDate, toDate }) => {
  let response = {};

  try {
    response = await axiosInstanceCached.get(
      `/clubhouse/${clubID}/metrics/users?fromDate=${fromDate || ''}&toDate=${toDate || ''}`,
      {
        id: `clubMetricUsers_${clubID}_${fromDate}_${toDate}`,
        cache: {
          ttl: 1000 * 60 * 15, //* 15 min
        },
      }
    );
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* clubhouseTopUsers return top clubmember stats
export const clubhouseTopUsers = async ({ clubID, fromDate, toDate }) => {
  let response = {};

  try {
    response = await axiosInstanceCached.get(
      `/clubhouse/${clubID}/metrics/users/top10?fromDate=${fromDate || ''}&toDate=${toDate || ''}`,
      {
        id: `clubMetricTopUsers_${clubID}_${fromDate}_${toDate}`,
        cache: {
          ttl: 1000 * 60 * 15, //* 15 min
        },
      }
    );
    // console.log('clubhouseTopUsers response:', response);
    // console.log('clubhouseTopUsers cached:', response.cached);
    // console.log('clubhouseTopUsers ttl:', response.config?.cache?.ttl);
    // console.log('clubhouseTopUsers config:', response.config);
    // console.log('clubhouseTopUsers duration:', response.duration);
    // console.log('============================');
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* clubhouseShotStats return club shot stats
export const clubhouseShotStats = async ({ clubID, fromDate, toDate }) => {
  let response = {};

  try {
    response = await axiosInstanceCached.get(
      `/clubhouse/${clubID}/metrics/shots?fromDate=${fromDate || ''}&toDate=${toDate || ''}`,
      {
        id: `clubMetricShotStats_${clubID}_${fromDate}_${toDate}`,
        cache: {
          ttl: 1000 * 60 * 5, //* 5 min
        },
      }
    );
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* clubhouseCourseStats return club course stats
export const clubhouseCourseStats = async ({ clubID, fromDate, toDate }) => {
  let response = {};

  try {
    response = await axiosInstanceCached.get(
      `/clubhouse/${clubID}/metrics/courses?fromDate=${fromDate || ''}&toDate=${toDate || ''}`,
      {
        id: `clubMetricCourseStats_${clubID}_${fromDate}_${toDate}`,
        cache: {
          ttl: 1000 * 60 * 15, //* 15 min
        },
      }
    );
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* clubhouseFavorites return club favorites
export const clubhouseFavorites = async ({ clubID, fromDate, toDate }) => {
  let response = {};

  try {
    response = await axiosInstanceCached.get(
      `/clubhouse/${clubID}/metrics/favorites?fromDate=${fromDate || ''}&toDate=${toDate || ''}`,
      {
        id: `clubMetricFavs_${clubID}_${fromDate}_${toDate}`,
        cache: {
          ttl: 1000 * 60 * 15, //* 15 min
        },
      }
    );
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* clubhouseEventStats return club events stats
export const clubhouseEventStats = async ({ clubID, fromDate, toDate }) => {
  let response = {};

  try {
    response = await axiosInstanceCached.get(
      `/clubhouse/${clubID}/metrics/events?fromDate=${fromDate || ''}&toDate=${toDate || ''}`,
      {
        id: `clubMetricEvents_${clubID}_${fromDate}_${toDate}`,
        cache: {
          ttl: 1000 * 60 * 15, //* 15 min
        },
      }
    );
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* clubhouseEventUserStats return club events stats
export const clubhouseEventUserStats = async ({ clubID, fromDate, toDate }) => {
  let response = {};

  try {
    response = await axiosInstanceCached.get(
      `/clubhouse/${clubID}/metrics/events/users?fromDate=${fromDate || ''}&toDate=${toDate || ''}`,
      {
        id: `clubMetricEventUsers_${clubID}_${fromDate}_${toDate}`,
        cache: {
          ttl: 1000 * 60 * 15, //* 15 min
        },
      }
    );
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* clubhouseGameModeStats return club events stats
export const clubhouseGameModeStats = async ({ clubID, fromDate = '', toDate = '' }) => {
  let response = {};

  try {
    response = await axiosInstanceCached.get(
      `/clubhouse/${clubID}/metrics/games?fromDate=${fromDate}&toDate=${toDate}`,
      {
        id: `clubMetricModeStats_${clubID}_${fromDate}_${toDate}`,
        cache: {
          ttl: 1000 * 60 * 15, //* 15 min
        },
      }
    );
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* clubhouseRoundStats return club round stats
export const clubhouseRoundStats = async ({
  clubID,
  fromDate = '',
  toDate = '',
  fromTime = '',
  toTime = '',
}) => {
  let response = {};
  const timeVar = 'T';

  const url = `/clubhouse/${clubID}/metrics/rounds?fromDate=${fromDate}${
    fromTime ? `${timeVar}${fromTime}` : ''
  }&toDate=${toDate}${toTime ? `${timeVar}${toTime}` : ''}`;

  try {
    response = await axiosInstance.get(url);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* clubhouseUsageStats return club usage stats
export const clubhouseUsageStats = async ({ clubID, fromDate = '', toDate = '' }) => {
  let response = {};

  try {
    response = await axiosInstance.get(
      `/clubhouse/${clubID}/metrics/simulators/usage?fromDate=${fromDate}&toDate=${toDate}`
    );
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

// @ Clubhouse Per Sim stats ==============================================

//* clubhouseSimUsers return club user stats by sim ID
export const clubhouseSimUsers = async ({ clubID, licenseID, fromDate = '', toDate = '' }) => {
  let response = {};

  try {
    response = await axiosInstance.get(
      `/clubhouse/${clubID}/metrics/simulators/users/${licenseID}?fromDate=${fromDate}&toDate=${toDate}`
    );
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* clubhouseSimTopUsers return club top users by sim ID
export const clubhouseSimTopUsers = async ({ clubID, licenseID, fromDate = '', toDate = '' }) => {
  let response = {};

  try {
    response = await axiosInstance.get(
      `/clubhouse/${clubID}/metrics/simulators/users/top10/${licenseID}?fromDate=${fromDate}&toDate=${toDate}`
    );
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* clubhouseSimShots return club shot stats by sim ID
export const clubhouseSimShots = async ({ clubID, licenseID, fromDate = '', toDate = '' }) => {
  let response = {};

  try {
    response = await axiosInstance.get(
      `/clubhouse/${clubID}/metrics/simulators/shots/${licenseID}?fromDate=${fromDate}&toDate=${toDate}`
    );
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* clubhouseSimCourses return club course stats by sim ID
export const clubhouseSimCourses = async ({ clubID, licenseID, fromDate = '', toDate = '' }) => {
  let response = {};

  try {
    response = await axiosInstance.get(
      `/clubhouse/${clubID}/metrics/simulators/courses/${licenseID}?fromDate=${fromDate}&toDate=${toDate}`
    );
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* clubhouseSimFavorites return club favorites by sim ID
export const clubhouseSimFavorites = async ({ clubID, licenseID, fromDate = '', toDate = '' }) => {
  let response = {};

  try {
    response = await axiosInstance.get(
      `/clubhouse/${clubID}/metrics/simulators/favorites/${licenseID}?fromDate=${fromDate}&toDate=${toDate}`
    );
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* clubhouseSimGameModes return club game mode stats by sim ID
export const clubhouseSimGameModes = async ({ clubID, licenseID, fromDate = '', toDate = '' }) => {
  let response = {};

  try {
    response = await axiosInstance.get(
      `/clubhouse/${clubID}/metrics/simulators/games/${licenseID}?fromDate=${fromDate}&toDate=${toDate}`
    );
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* clubhouseSimRounds return club round stats by sim ID
export const clubhouseSimRounds = async ({
  clubID,
  licenseID,
  fromDate = '',
  toDate = '',
  fromTime = '',
  toTime = '',
}) => {
  let response = {};
  const timeVar = 'T';

  const url = `/clubhouse/${clubID}/metrics/simulators/rounds/${licenseID}?fromDate=${fromDate}${
    fromTime ? `${timeVar}${fromTime}` : ''
  }&toDate=${toDate}${toTime ? `${timeVar}${toTime}` : ''}`;

  try {
    response = await axiosInstance.get(url);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* clubhouseSimUsage return club user stats by sim ID
export const clubhouseSimUsage = async ({ clubID, licenseID, fromDate = '', toDate = '' }) => {
  let response = {};

  try {
    response = await axiosInstance.get(
      `/clubhouse/${clubID}/metrics/simulators/usage/${licenseID}?fromDate=${fromDate}&toDate=${toDate}`
    );
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};
